import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusIcon, PencilIcon, TrashIcon, BeakerIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Ingredients = () => {
  const [ingredients, setIngredients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    ingredient_type: '',
    unit_of_measure: '',
    cost_per_unit: '',
    price_update_date: new Date().toISOString().split('T')[0], // Data corrente in formato YYYY-MM-DD
    alcohol_percentage: '',
    density: '1.0',
    notes: ''
  });

  const ingredientTypes = [
    { value: 'alcohol', label: 'Alcool' },
    { value: 'water', label: 'Acqua' },
    { value: 'sugar', label: 'Zucchero' },
    { value: 'aroma', label: 'Aroma' },
    { value: 'infusion', label: 'Infuso' },
    { value: 'other', label: 'Altro' }
  ];

  const unitOfMeasure = [
    { value: 'kg', label: 'Chilogrammi (kg)' },
    { value: 'l', label: 'Litri (l)' },
    { value: 'ml', label: 'Millilitri (ml)' },
    { value: 'g', label: 'Grammi (g)' },
    { value: 'pieces', label: 'Pezzi' }
  ];

  useEffect(() => {
    loadIngredients();
  }, []);

  const loadIngredients = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/ingredients`);
      setIngredients(response.data);
    } catch (error) {
      console.error('Errore nel caricamento degli ingredienti:', error);
      toast.error('Errore nel caricamento degli ingredienti');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        cost_per_unit: parseFloat(formData.cost_per_unit) || 0,
        alcohol_percentage: parseFloat(formData.alcohol_percentage) || 0,
        density: parseFloat(formData.density) || 1.0
      };

      if (editingIngredient) {
        await axios.put(`${API}/ingredients/${editingIngredient.id}`, payload);
        toast.success('Ingrediente aggiornato con successo');
      } else {
        await axios.post(`${API}/ingredients`, payload);
        toast.success('Ingrediente creato con successo');
      }

      await loadIngredients();
      resetForm();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Errore nel salvataggio:', error);
      toast.error('Errore nel salvataggio dell\'ingrediente');
    }
  };

  const handleEdit = (ingredient) => {
    setEditingIngredient(ingredient);
    setFormData({
      code: ingredient.code,
      name: ingredient.name,
      ingredient_type: ingredient.ingredient_type,
      unit_of_measure: ingredient.unit_of_measure,
      cost_per_unit: ingredient.cost_per_unit.toString(),
      price_update_date: ingredient.price_update_date ? new Date(ingredient.price_update_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      alcohol_percentage: ingredient.alcohol_percentage?.toString() || '0',
      density: ingredient.density?.toString() || '1.0',
      notes: ingredient.notes || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo ingrediente?')) {
      return;
    }

    try {
      await axios.delete(`${API}/ingredients/${id}`);
      toast.success('Ingrediente eliminato con successo');
      await loadIngredients();
    } catch (error) {
      console.error('Errore nell\'eliminazione:', error);
      toast.error('Errore nell\'eliminazione dell\'ingrediente');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      ingredient_type: '',
      unit_of_measure: '',
      cost_per_unit: '',
      price_update_date: new Date().toISOString().split('T')[0],
      alcohol_percentage: '',
      density: '1.0',
      notes: ''
    });
    setEditingIngredient(null);
  };

  const getIngredientTypeBadge = (type) => {
    const typeConfig = {
      alcohol: { variant: 'destructive', label: 'Alcool' },
      water: { variant: 'secondary', label: 'Acqua' },
      sugar: { variant: 'outline', label: 'Zucchero' },
      aroma: { variant: 'default', label: 'Aroma' },
      infusion: { variant: 'secondary', label: 'Infuso' },
      other: { variant: 'outline', label: 'Altro' }
    };

    const config = typeConfig[type] || typeConfig.other;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(value);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ingredients-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Ingredienti</h1>
          <p className="text-gray-600">Gestisci gli ingredienti base per le ricette</p>
        </div>
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-ingredient-btn">
              <PlusIcon className="w-4 h-4 mr-2" />
              Nuovo Ingrediente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingIngredient ? 'Modifica Ingrediente' : 'Nuovo Ingrediente'}
              </DialogTitle>
              <DialogDescription>
                Inserisci i dettagli dell'ingrediente. I campi con * sono obbligatori.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Codice *</Label>
                  <Input
                    id="code"
                    data-testid="ingredient-code-input"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    placeholder="Es. ALC96.3"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Nome *</Label>
                  <Input
                    id="name"
                    data-testid="ingredient-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Es. Alcool 96.3"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ingredient_type">Tipo Ingrediente *</Label>
                  <Select
                    value={formData.ingredient_type}
                    onValueChange={(value) => setFormData({...formData, ingredient_type: value})}
                  >
                    <SelectTrigger data-testid="ingredient-type-select">
                      <SelectValue placeholder="Seleziona tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {ingredientTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit_of_measure">Unità di Misura *</Label>
                  <Select
                    value={formData.unit_of_measure}
                    onValueChange={(value) => setFormData({...formData, unit_of_measure: value})}
                  >
                    <SelectTrigger data-testid="unit-measure-select">
                      <SelectValue placeholder="Seleziona unità" />
                    </SelectTrigger>
                    <SelectContent>
                      {unitOfMeasure.map((unit) => (
                        <SelectItem key={unit.value} value={unit.value}>
                          {unit.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cost_per_unit">Costo per Unità (€) *</Label>
                  <Input
                    id="cost_per_unit"
                    data-testid="ingredient-cost-input"
                    type="number"
                    step="0.01"
                    value={formData.cost_per_unit}
                    onChange={(e) => setFormData({...formData, cost_per_unit: e.target.value})}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price_update_date">Data Aggiornamento Prezzo *</Label>
                  <Input
                    id="price_update_date"
                    data-testid="ingredient-price-date-input"
                    type="date"
                    value={formData.price_update_date}
                    onChange={(e) => setFormData({...formData, price_update_date: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="alcohol_percentage">% Alcol (TAV)</Label>
                  <Input
                    id="alcohol_percentage"
                    data-testid="ingredient-alcohol-input"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={formData.alcohol_percentage}
                    onChange={(e) => setFormData({...formData, alcohol_percentage: e.target.value})}
                    placeholder="0.0"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="density">Densità (kg/L)</Label>
                  <Input
                    id="density"
                    data-testid="ingredient-density-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.density}
                    onChange={(e) => setFormData({...formData, density: e.target.value})}
                    placeholder="1.0"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Note</Label>
                <Input
                  id="notes"
                  data-testid="ingredient-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Note aggiuntive sull'ingrediente"
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  data-testid="cancel-ingredient-btn"
                >
                  Annulla
                </Button>
                <Button type="submit" data-testid="save-ingredient-btn">
                  {editingIngredient ? 'Aggiorna' : 'Salva'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Input
              type="text"
              placeholder="🔍 Cerca per nome o codice..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-4"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="text-sm text-gray-500 mt-2">
              {ingredients.filter(ing => 
                ing.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                ing.code.toLowerCase().includes(searchQuery.toLowerCase())
              ).length} risultati trovati
            </p>
          )}
        </CardContent>
      </Card>

      {/* Ingredients List */}
      {ingredients.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <BeakerIcon className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun ingrediente</h3>
            <p className="text-gray-500 text-center mb-6">
              Inizia creando il tuo primo ingrediente per iniziare a costruire le ricette.
            </p>
            <Button onClick={() => setIsModalOpen(true)}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Crea primo ingrediente
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {ingredients
            .filter(ingredient => 
              ingredient.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              ingredient.code.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map((ingredient) => (
            <Card key={ingredient.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <CardTitle className="text-lg">{ingredient.name}</CardTitle>
                      {getIngredientTypeBadge(ingredient.ingredient_type)}
                    </div>
                    <CardDescription>
                      Codice: {ingredient.code} • {formatCurrency(ingredient.cost_per_unit)}/{ingredient.unit_of_measure}
                      {ingredient.alcohol_percentage > 0 && (
                        <> • {ingredient.alcohol_percentage}% vol</>
                      )}
                      <br />
                      Aggiornamento prezzo: {new Date(ingredient.price_update_date).toLocaleDateString('it-IT')}
                    </CardDescription>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(ingredient)}
                      data-testid={`edit-ingredient-${ingredient.id}`}
                    >
                      <PencilIcon className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(ingredient.id)}
                      data-testid={`delete-ingredient-${ingredient.id}`}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {ingredient.notes && (
                <CardContent>
                  <p className="text-sm text-gray-600">{ingredient.notes}</p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Ingredients;